"use client";

import { useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import SocialLoginButtons from "./SocialLoginButtons";
import { Checkbox } from "./FormControls";
import {
  CAPTCHA_REQUIRED_MESSAGE,
  CAPTCHA_REQUIRED_MESSAGE_EN,
  getCaptchaErrorMessage,
} from "@/lib/turnstile-client";
import TurnstileWidget from "./TurnstileWidget";
import { isTurnstileWidgetEnabled } from "@/lib/turnstile-widget";
import { rememberBrowserAuthHint } from "@/lib/auth-hint";
import type { OAuthProvider } from "@/lib/oauth/config";
import { toLocalizedPath, type Locale } from "@/lib/i18n";
import {
  USER_GENDER_LABELS,
  USER_GENDER_LABELS_EN,
  USER_GENDERS,
  type UserGender,
} from "@/lib/user-gender";

type EnabledProviders = Record<OAuthProvider, boolean>;

type Props = {
  redirectTo: string;
  locale?: Locale;
  enabled?: EnabledProviders;
  hasReferralLink?: boolean;
};

export default function SignupForm({
  redirectTo,
  locale = "ko",
  enabled = { google: false, kakao: false, naver: false, apple: false },
  hasReferralLink = false,
}: Props) {
  const { refresh, replace } = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [gender, setGender] = useState<UserGender | "">("");
  const [referralCode, setReferralCode] = useState("");
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const turnstileTokenRef = useRef<string | null>(null);
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const [isPending, startTransition] = useTransition();
  const showSocial = Object.values(enabled).some(Boolean);
  const copy = locale === "en"
    ? { missing: "Enter your email and password.", short: "Password must be at least 8 characters.", mismatch: "Passwords do not match.", agreements: "Please agree to the Terms of Service and Privacy Policy.", email: "Email", name: "Display name", optional: "(optional)", gender: "Gender", noSelection: "Prefer not to say", referral: "Referral code", password: "Password", confirm: "Confirm password", divider: "or sign up with email", agreementHint: "Agree to the terms to sign up with a social account.", pending: "Signing up…", submit: "Sign up", already: "Already have an account?", login: "Log in", terms: "Terms of Service", privacy: "Privacy Policy" }
    : { missing: "이메일과 비밀번호를 모두 입력해 주세요.", short: "비밀번호는 8자 이상이어야 해요.", mismatch: "비밀번호 확인이 일치하지 않아요.", agreements: "이용약관과 개인정보처리방침에 동의해 주세요.", email: "이메일", name: "닉네임", optional: "(선택)", gender: "성별", noSelection: "선택 안 함", referral: "추천 코드", password: "비밀번호", confirm: "비밀번호 확인", divider: "또는 이메일로 가입", agreementHint: "약관에 동의하면 소셜 계정으로 바로 가입할 수 있어요.", pending: "가입 중…", submit: "회원가입", already: "이미 계정이 있나요?", login: "로그인", terms: "이용약관", privacy: "개인정보처리방침" };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || !password) {
      setError(copy.missing);
      return;
    }
    if (password.length < 8) {
      setError(copy.short);
      return;
    }
    if (password !== passwordConfirm) {
      setError(copy.mismatch);
      return;
    }
    if (!agreedTerms || !agreedPrivacy) {
      setError(copy.agreements);
      return;
    }
    const turnstileToken = turnstileTokenRef.current;
    if (isTurnstileWidgetEnabled() && !turnstileToken) {
      setError(locale === "en" ? CAPTCHA_REQUIRED_MESSAGE_EN : CAPTCHA_REQUIRED_MESSAGE);
      return;
    }
    setError(null);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          name: name.trim() || undefined,
          gender: gender || undefined,
          agreedTerms,
          agreedPrivacy,
          referralCode: hasReferralLink ? undefined : referralCode.trim() || undefined,
          turnstileToken,
        }),
      });
      if (res.ok) {
        rememberBrowserAuthHint();
        startTransition(() => {
          replace(redirectTo);
          refresh();
        });
        return;
      }
      const captchaError = await getCaptchaErrorMessage(res, locale);
      if (captchaError) {
        setError(captchaError);
        return;
      }
      if (res.status === 409) {
        setError(locale === "en" ? "This email is already in use." : "이미 사용 중인 이메일이에요.");
        return;
      }
      if (res.status === 429) {
        setError(locale === "en" ? "Too many sign-up attempts. Please try again later." : "가입 시도가 많습니다. 잠시 후 다시 시도해 주세요.");
        return;
      }
      if (res.status === 400) {
        setError(locale === "en" ? "Please check your input." : "입력값을 다시 확인해 주세요.");
        return;
      }
      setError(locale === "en" ? "Sign-up failed. Please try again later." : "회원가입에 실패했어요. 잠시 후 다시 시도해 주세요.");
    } catch {
      setError(locale === "en" ? "A network error occurred. Please try again later." : "네트워크 오류가 발생했어요. 잠시 후 다시 시도해 주세요.");
    } finally {
      turnstileTokenRef.current = null;
      setTurnstileResetSignal((value) => value + 1);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex w-full max-w-sm flex-col gap-4 rounded-3xl bg-white/80 p-6 shadow-pop"
      noValidate
    >
      {/* 소셜 가입이 최단 경로(동의 2개 + 탭 1회)라 폼 최상단에 둔다.
          소셜 버튼이 필수 동의 상태를 필요로 해 동의 블록이 그 위에 온다. */}
      <div className="flex flex-col gap-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
        <Checkbox
          size="sm"
          checked={agreedTerms}
          onChange={setAgreedTerms}
          disabled={isPending}
          label={
            <>
            <Link href={toLocalizedPath("/legal/terms", locale)} target="_blank" className="underline">
                {copy.terms}
              </Link>
              {locale === "en" ? " (required)" : "에 동의합니다. (필수)"}
            </>
          }
        />
        <Checkbox
          size="sm"
          checked={agreedPrivacy}
          onChange={setAgreedPrivacy}
          disabled={isPending}
          label={
            <>
            <Link href={toLocalizedPath("/legal/privacy", locale)} target="_blank" className="underline">
                {copy.privacy}
              </Link>
              {locale === "en" ? " (required)" : "에 동의합니다. (필수)"}
            </>
          }
        />
      </div>

      {showSocial && (
        <>
          <SocialLoginButtons
            mode="signup"
            redirectTo={redirectTo}
            enabled={enabled}
            locale={locale}
            agreedTerms={agreedTerms}
            agreedPrivacy={agreedPrivacy}
            gender={gender}
          />
          {!(agreedTerms && agreedPrivacy) && (
            <p className="text-center text-xs text-slate-400">
              {copy.agreementHint}
            </p>
          )}
          <div className="relative my-1 flex items-center text-xs text-slate-400">
            <span className="flex-1 border-t border-slate-200" />
            <span className="mx-3">{copy.divider}</span>
            <span className="flex-1 border-t border-slate-200" />
          </div>
        </>
      )}

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.email}
        <input
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isPending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.name} <span className="text-xs text-slate-400">{copy.optional}</span>
        <input
          type="text"
          name="name"
          autoComplete="nickname"
          maxLength={40}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isPending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.gender} <span className="text-xs text-slate-400">{copy.optional}</span>
        <select
          name="gender"
          autoComplete="sex"
          value={gender}
          onChange={(e) => setGender(e.target.value as UserGender | "")}
          disabled={isPending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        >
          <option value="">{copy.noSelection}</option>
          {USER_GENDERS.map((value) => (
            <option key={value} value={value}>
              {(locale === "en" ? USER_GENDER_LABELS_EN : USER_GENDER_LABELS)[value]}
            </option>
          ))}
        </select>
      </label>
      {!hasReferralLink && (
        <label className="flex flex-col gap-1 text-sm text-slate-700">
          {copy.referral} <span className="text-xs text-slate-400">{copy.optional}</span>
          <input
            type="text"
            name="referralCode"
            autoComplete="off"
            maxLength={10}
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            disabled={isPending}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm uppercase text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
          />
        </label>
      )}
      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.password} <span className="text-xs text-slate-400">{locale === "en" ? "(8+ characters)" : "(8자 이상)"}</span>
        <input
          type="password"
          name="password"
          autoComplete="new-password"
          minLength={8}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isPending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.confirm}
        <input
          type="password"
          name="password-confirm"
          autoComplete="new-password"
          required
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          disabled={isPending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>

      <TurnstileWidget
        action="signup"
        onTokenChange={(token) => {
          turnstileTokenRef.current = token;
        }}
        resetSignal={turnstileResetSignal}
      />

      {error && (
        <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={isPending}
        className="rounded-xl bg-brand-gradient py-2 text-sm font-medium text-white shadow-pop disabled:opacity-60"
      >
        {isPending ? copy.pending : copy.submit}
      </button>

      <p className="text-center text-xs text-slate-500">
        {copy.already}{" "}
        <Link
          href={
            redirectTo === "/"
              ? toLocalizedPath("/login", locale)
              : `${toLocalizedPath("/login", locale)}?from=${encodeURIComponent(redirectTo)}`
          }
          className="font-medium text-brand-600 underline"
        >
          {copy.login}
        </Link>
      </p>
    </form>
  );
}
